"use client";

import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api/client";
import type { SkillTreeGoal } from "@/app/api/skill-tree/goals/route";
import Glyph from "@/components/ui/Glyph";
import { useUiTexts } from "@/lib/ui-texts-client";

export const TYPE_ICON: Record<string, string> = {
  hand_limit:      "✋",
  adventure_limit: "🧭",
  spirit:          "✨",
  material:        "💎",
  background:      "🏛️",
  novel:           "📖",
};

export function ConditionBar({ label, unit, current, threshold, done, onPaper = false }: {
  label: string; unit: string; current: number; threshold: number; done: boolean;
  /** 報酬一覧の厚紙の札の上に置く。字を濃い色に、ゲージを印刷の枠と鉛筆の斜線にする */
  onPaper?: boolean;
}) {
  if (threshold === 0) return null; // free 条件は非表示
  const pct = Math.min(Math.round((current / threshold) * 100), 100);
  const doneText = onPaper ? "text-[#2f6b45]" : "text-emerald-400";
  const text = onPaper ? "" : "text-[var(--foreground)]";
  return (
    // ノートの紙の上では、罫線の1行（28px）に名前とゲージを収める（報酬一覧の頁・10/12）
    <div className={onPaper ? "flex h-[28px] flex-col justify-center gap-[3px]" : "space-y-0.5"}>
      <div className={`flex justify-between text-[11px] ${onPaper ? "leading-[16px]" : ""}`}>
        <span className={done ? doneText : `${text} opacity-60`}>{label}</span>
        <span className={done ? doneText : `${text} opacity-50`}>
          {current}/{threshold}{unit}
        </span>
      </div>
      {onPaper ? (
        // 厚紙の札の上では、印刷の細い枠に鉛筆の斜線（globals.css の .library-gauge・10/18）
        <div className="library-gauge w-full" data-thin="" data-done={done ? "" : undefined}>
          <span style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="h-1 w-full rounded-full bg-white/10">
          <div className={`h-1 rounded-full transition-all ${done ? "bg-emerald-400" : "bg-violet-400"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function GoalCard({ goal, rank }: { goal: SkillTreeGoal; rank: number }) {
  const { t } = useUiTexts();
  const visibleConds = goal.conditions.filter((c) => c.threshold > 0);
  return (
    <div className={`rounded-xl border p-3 space-y-2 transition-all ${
      goal.isReady
        ? "border-emerald-500/60 bg-emerald-500/10"
        : rank === 0
          ? "border-violet-500/50 bg-[var(--background)]/65 backdrop-blur-sm"
          : "border-white/15 bg-[var(--background)]/50 backdrop-blur-sm"
    }`}>
      {/* ヘッダー */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base shrink-0"><Glyph e={TYPE_ICON[goal.type] ?? "🔓"} /></span>
          <div className="min-w-0">
            <p className={`text-sm font-semibold truncate ${
              goal.isReady ? "text-emerald-300" : "text-[var(--foreground)]"
            }`}>{goal.name}</p>
            <p className="text-[11px] text-[var(--foreground)] opacity-50 truncate">{goal.rewardLabel}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {goal.isReady ? (
            <span className="text-[11px] font-bold text-emerald-400 bg-emerald-400/20 px-2 py-0.5 rounded-full">{t("quests.goals.full.cardReady")}</span>
          ) : (
            <span className="text-sm font-bold text-violet-300">{goal.progress}%</span>
          )}
        </div>
      </div>

      {/* 全体プログレスバー */}
      {!goal.isReady && (
        <div className="h-1.5 w-full rounded-full bg-white/10">
          <div
            className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-indigo-400 transition-all"
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      )}

      {/* 条件詳細 */}
      {visibleConds.length > 0 && (
        <div className="space-y-1.5">
          {visibleConds.map((c, i) => (
            <ConditionBar key={i} {...c} />
          ))}
        </div>
      )}
    </div>
  );
}

/** 狭い列に収める1行の目標（ホームの右の列） */
function CompactGoalRow({ goal }: { goal: SkillTreeGoal }) {
  const { t } = useUiTexts();
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 text-sm"><Glyph e={TYPE_ICON[goal.type] ?? "🔓"} /></span>
        <p className={`min-w-0 flex-1 truncate text-xs font-semibold ${
          goal.isReady ? "text-emerald-400" : "text-[var(--foreground)] opacity-90"
        }`}>
          {goal.name}
        </p>
        {goal.isReady ? (
          <span className="shrink-0 rounded-full bg-emerald-400/20 px-1.5 text-[10px] font-bold text-emerald-400">{t("quests.goals.compact.rowReady")}</span>
        ) : (
          <span className="shrink-0 text-[11px] font-bold text-violet-300">{goal.progress}%</span>
        )}
      </div>
      {!goal.isReady && (
        <div className="h-1 w-full rounded-full bg-white/10">
          <div
            className="h-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-400"
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default function SkillTreeGoals({ token, compact = false, line = false, onOpenAll }: {
  token: string;
  compact?: boolean;
  /** 1行だけにする（ホーム。中身はクエスト画面の解放目標のタブで見る）。onOpenAll と一緒に使う */
  line?: boolean;
  /** 渡すと、小さくまとめた形の下に「すべて見る」を出し、欄を押すとクエスト画面へ行く */
  onOpenAll?: () => void;
}) {
  const { t } = useUiTexts();
  const [goals, setGoals] = useState<SkillTreeGoal[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ goals: SkillTreeGoal[] }>("/api/skill-tree/goals", token)
      .then((r) => {
        setGoals((r?.goals ?? []).filter((g) => !g.isUnlocked));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  // 1行の形: 「解放目標」と、一番達成に近い目標（印・名前・%）。押すとクエスト画面へ（達成した報酬はプレゼントボックスに届くので「受け取れる」は無い・10/22）
  if (line && onOpenAll) {
    const nearest = goals
      .reduce<SkillTreeGoal | null>((best, g) => (!best || g.progress > best.progress ? g : best), null);
    return (
      <button
        onClick={onOpenAll}
        className="flex w-full items-baseline gap-2 rounded-xl border border-white/15 bg-[var(--background)]/75 px-2.5 py-2 text-left text-[var(--foreground)] backdrop-blur-sm transition-all active:scale-[0.98]"
      >
        <span className="shrink-0 text-xs font-semibold opacity-90">{t("quests.goals.line.title")}</span>
        <span className="flex min-w-0 flex-1 justify-end text-[10px]">
          {loading ? (
            <span className="opacity-50">{t("quests.goals.line.loading")}</span>
          ) : nearest ? (
            <>
              <span className="min-w-0 truncate">
                <span className="mr-0.5"><Glyph e={TYPE_ICON[nearest.type] ?? "🔓"} /></span>
                {nearest.name}
              </span>
              <span className={`ml-1 shrink-0 font-bold ${nearest.isReady ? "text-emerald-400" : "text-violet-300"}`}>
                {nearest.isReady ? t("quests.goals.line.ready") : `${nearest.progress}%`} ›
              </span>
            </>
          ) : (
            <span className="opacity-60">{t("quests.goals.line.all")}</span>
          )}
        </span>
      </button>
    );
  }

  // ホームの欄（クエスト画面への入口）は、読み込み中や0件のときも消さない。いつでもクエスト画面へ行ける
  if (compact && onOpenAll && (loading || goals.length === 0)) {
    return (
      <div className="w-full space-y-2 rounded-xl border border-white/15 bg-[var(--background)]/75 p-2.5 backdrop-blur-sm">
        <p className="text-xs font-semibold text-[var(--foreground)] opacity-90">{t("quests.goals.compact.title")}</p>
        <p className="text-[11px] text-[var(--foreground)] opacity-50">
          {loading ? t("quests.goals.compact.loading") : t("quests.goals.compact.empty")}
        </p>
        <button
          onClick={onOpenAll}
          className="w-full text-center text-[11px] text-violet-300 transition-opacity hover:opacity-80"
        >
          {t("quests.goals.compact.openAll")}
        </button>
      </div>
    );
  }
  if (loading) return null;
  if (goals.length === 0) return null;

  const shown = expanded ? goals : goals.slice(0, 3);
  const readyCount = goals.filter((g) => g.isReady).length;

  // 小さくまとめた形: 1枚の枠に1行ずつ
  if (compact) {
    return (
      <div className="w-full space-y-2 rounded-xl border border-white/15 bg-[var(--background)]/75 p-2.5 backdrop-blur-sm">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-[var(--foreground)] opacity-90">{t("quests.goals.compact.title")}</p>
          <span className="text-[10px] text-[var(--foreground)] opacity-50">
            {readyCount > 0 ? <span className="text-emerald-400">{t("quests.goals.compact.ready", { 件数: readyCount })}</span> : t("quests.goals.compact.count", { 件数: goals.length })}
          </span>
        </div>
        {shown.map((g) => (
          <CompactGoalRow key={g.id} goal={g} />
        ))}
        {onOpenAll ? (
          <button
            onClick={onOpenAll}
            className="w-full text-center text-[11px] text-violet-300 transition-opacity hover:opacity-80"
          >
            {t("quests.goals.compact.openAll")}
          </button>
        ) : goals.length > 3 && (
          <button
            onClick={() => setExpanded((p) => !p)}
            className="w-full text-center text-[11px] text-[var(--foreground)] opacity-50 transition-opacity hover:opacity-80"
          >
            {expanded ? t("quests.goals.compact.collapse") : t("quests.goals.compact.more", { 件数: goals.length - 3 })}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-md space-y-2">
      {/* セクションヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)] opacity-90">{t("quests.goals.full.title")}</p>
          {readyCount > 0 && (
            <p className="text-[11px] text-emerald-400">{t("quests.goals.full.ready", { 件数: readyCount })}</p>
          )}
        </div>
        <span className="text-[11px] text-[var(--foreground)] opacity-40">{t("quests.goals.full.count", { 件数: goals.length })}</span>
      </div>

      {/* ゴール一覧 */}
      <div className="space-y-1.5">
        {shown.map((g, i) => (
          <GoalCard key={g.id} goal={g} rank={i} />
        ))}
      </div>

      {/* 展開ボタン */}
      {goals.length > 3 && (
        <button
          onClick={() => setExpanded((p) => !p)}
          className="w-full text-center text-sm text-[var(--foreground)] opacity-40 hover:opacity-70 py-1 transition-opacity"
        >
          {expanded ? t("quests.goals.full.collapse") : t("quests.goals.full.more", { 件数: goals.length - 3 })}
        </button>
      )}
    </div>
  );
}
